import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  GlassCard,
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { usePersona } from '@/app/providers';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { DocumentTree } from './DocumentTree';
import { DocumentPane } from './DocumentPane';
import { RevisionPanel } from './RevisionPanel';
import { TrainingCompliance } from './TrainingCompliance';
import { PendingChanges } from './PendingChanges';
import { DocumentsBand, DocumentsHeader } from './DocumentsBand';
import { trainingComplianceBand } from './TrainingCompliance.transform';
import {
  DOCUMENT_TABS,
  allDocumentIds,
  defaultDocumentFor,
  documentTree,
  documentView,
  documentsCopy,
  documentsHeaderFacts,
  documentsTabFromSearchParam,
  documentsTabHeadlines,
  revisionContext,
  type DocumentsTab,
} from './Scene.transform';

/** The library's three columns share one height: the space actually left under
 *  the band and tabs (comp adjudication 3, ~535 px at 1470×833). Sized by the
 *  flex body, never a fixed `100dvh` subtraction — a wrapped band shrinks it
 *  instead of scrolling the page. */
export function DocumentsScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const copy = documentsCopy();
  const { persona } = usePersona();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<DocumentsTab>('compliance');

  const validIds = useMemo(() => new Set(allDocumentIds()), []);
  const docParam = searchParams.get('doc');
  const selectedId = docParam && validIds.has(docParam) ? docParam : defaultDocumentFor(persona);

  // `?tab=` is the tab's source of truth on a fresh load, and re-fires when a
  // row click rewrites it — the same idiom as the compliance scene's tabs.
  useEffect(() => {
    const requestedTab = documentsTabFromSearchParam(searchParams.get('tab'));
    if (requestedTab) setTab(requestedTab);
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const groups = useMemo(() => documentTree(), []);
  const facts = useMemo(() => documentsHeaderFacts(), []);
  const band = useMemo(() => trainingComplianceBand(), []);
  const headlines = useMemo(() => documentsTabHeadlines(), []);
  const tabItems = DOCUMENT_TABS.map((item) => ({ ...item, count: headlines[item.id] }));
  const view = useMemo(() => documentView(selectedId), [selectedId]);
  const context = useMemo(() => revisionContext(selectedId), [selectedId]);

  // A tree click pins `?doc=`, which would otherwise permanently beat the persona default.
  // Once the persona actually changes (not on first mount, so a deep link still wins),
  // drop the pin and let the new persona's default take over (MF2).
  const previousPersonaRef = useRef(persona);
  useEffect(() => {
    if (previousPersonaRef.current === persona) return;
    previousPersonaRef.current = persona;
    if (searchParams.has('doc')) {
      const next = new URLSearchParams(searchParams);
      next.delete('doc');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona]);

  const handleSelect = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('doc', id);
    setSearchParams(next, { replace: true });
  };

  // A tab click writes `?tab=` so the URL stays the source of truth: a later
  // `setSearchParams` (the persona effect dropping `doc`) would otherwise
  // re-hydrate the tab from a stale param and snap the user back.
  const selectTab = (next: DocumentsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  // A matrix column header opens its document where documents live.
  const openOnLibrary = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('doc', id);
    next.set('tab', 'library');
    setSearchParams(next, { replace: true });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="DocumentsScene">
        <SurfacePageBody className="flex h-full min-h-0 flex-col gap-3 pb-6" width="full">
          <DocumentsHeader
            title={copy.title}
            facts={facts}
            actions={<HelpAnchor doc="documents/overview" label="Documents" />}
          />

          <Commentable name="DocumentsBand">
            <DocumentsBand cells={band} />
          </Commentable>

          <SurfacePageTabs
            aria-label="Documents"
            items={tabItems}
            value={tab}
            onChange={selectTab}
            variant="ink"
            className="shrink-0"
          />

          {tab === 'library' ? (
            <div className="grid min-h-0 flex-1 grid-cols-[16.75rem_minmax(0,1fr)_19.75rem] gap-3.5" data-library>
              <Commentable name="DocumentTree">
                <GlassCard tone="matte" className="h-full min-h-0 overflow-y-auto p-0">
                  <DocumentTree groups={groups} selectedId={selectedId} onSelect={handleSelect} />
                </GlassCard>
              </Commentable>

              <Commentable name="DocumentPane">
                <GlassCard tone="matte" className="h-full min-h-0 min-w-0 overflow-y-auto bg-[var(--surface-1)] p-0">
                  <DocumentPane doc={view} />
                </GlassCard>
              </Commentable>

              <Commentable name="RevisionPanel">
                <GlassCard tone="matte" className="h-full min-h-0 overflow-y-auto p-0">
                  <RevisionPanel context={context} />
                </GlassCard>
              </Commentable>
            </div>
          ) : null}

          {tab === 'compliance' ? (
            <div className="shrink-0">
              <TrainingCompliance onSelectDocument={openOnLibrary} />
            </div>
          ) : null}

          {tab === 'ai' ? (
            <div className="shrink-0">
              <PendingChanges onSelectDocument={openOnLibrary} />
            </div>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
